import React from "react";
import { Card, Col, Row, ProgressBar } from "react-bootstrap";
import { FaCircle, FaLock, FaSquare } from "react-icons/fa";
import { BiSolidUpArrow } from "react-icons/bi";
import { IoLogoGoogle } from "react-icons/io5";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import "../audit_style.css";

interface WebsiteScoreCardProps {
  websiteScore: number;
  siteTitle: string | null;
  crawledPages: number;
  indexedPages: number;
  safeBrowsingStatus: string;
  selectTab: string;
  criticalCollected: { name: string; value: number }[];
  warningCollected: { name: string; value: number }[];
  noticesCollected: { name: string; value: number }[];
  passedCollected: { name: string; value: number }[];
  criticalPercentage: string;
  warningPercentage: string;
  noticesPercentage: string;
  passedPercentage: string;
  setSelectTab: (tab: string) => void;
  setActiveSide: (side: string | null) => void;
  setShowCalculationModal: (show: boolean) => void;
}

export const WebsiteScoreCard: React.FC<WebsiteScoreCardProps> = ({
  websiteScore,
  siteTitle,
  crawledPages,
  indexedPages,
  safeBrowsingStatus,
  selectTab,
  criticalCollected,
  warningCollected,
  noticesCollected,
  passedCollected,
  criticalPercentage,
  warningPercentage,
  noticesPercentage,
  passedPercentage,
  setSelectTab,
  setActiveSide,
  setShowCalculationModal,
}) => {
  const gradientId = "progressGradient";

  return (
    <Card
      className="p-4 mt-3"
      style={{
        maxWidth: "100%",
        boxShadow:
          "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px",
      }}
    >
      <Row>
        <Col>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "2rem",
            }}
          >
            <div
              style={{
                width: 150,
                height: 150,
                marginBottom: "3rem",
              }}
            >
              <svg style={{ height: 0 }}>
                <defs>
                  <linearGradient
                    id={gradientId}
                    gradientTransform="rotate(90)"
                  >
                    <stop offset="0%" stopColor="#00FF00" />
                    <stop offset="50%" stopColor="#FFD700" />
                    <stop offset="100%" stopColor="#FF4500" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <div style={{ width: "100%" }}>
              <p
                style={{
                  marginBottom: "0.3rem",
                  fontSize: "17px",
                  lineHeight: 0.1,
                }}
              >
                Website score for
              </p>
              <div
                style={{
                  marginBottom: "0.3rem",
                  fontWeight: 600,
                  fontSize: "25px",
                }}
              >
                {siteTitle}
              </div>
              <div
                className="hC_class"
                style={{ cursor: "pointer", color: "#327aee" }}
                onClick={() => setShowCalculationModal(true)}
              >
                How is it calculated?
              </div>
            </div>
            <div
              style={{
                minWidth: "25rem",
                width: "25rem",
                borderRadius: "7px",
                padding: "1rem",
                backgroundColor: "#fbfbfb",
                fontSize: "16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <p style={{ width: "100%", marginBottom: "0.3rem" }}>
                  Crawled pages
                </p>
                <div
                  style={{
                    width: "100%",
                    textAlign: "end",
                    fontWeight: 500,
                  }}
                >
                  {crawledPages}
                </div>
              </div>
              <hr />
              <div style={{ display: "flex", alignItems: "center" }}>
                <p style={{ width: "100%", marginBottom: "0.3rem" }}>
                  <IoLogoGoogle style={{ marginRight: "0.3rem" }} />
                  Google indexed pages
                </p>
                <div
                  style={{
                    width: "100%",
                    textAlign: "end",
                    fontWeight: 500,
                  }}
                >
                  {indexedPages}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <p style={{ width: "100%", marginBottom: "0.3rem" }}>
                  <FaLock style={{ marginRight: "0.3rem" }} />
                  Google safe browsing
                </p>
                <div
                  style={{
                    width: "100%",
                    textAlign: "end",
                    fontWeight: 500,
                  }}
                >
                  {safeBrowsingStatus}
                </div>
              </div>
            </div>
          </div>
          <hr />
          <Row>
            <Col>
              <div
                style={{
                  padding: "5px",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
                className={`tab_box tab_box_all ${
                  selectTab === "all" ? "tab_box_all_active" : ""
                }`}
                onClick={() => {
                  setSelectTab("all");
                  setActiveSide(null);
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "0.5rem",
                  }}
                >
                  <div>All Issue</div>
                  <div style={{ marginLeft: "auto" }}>100%</div>
                </div>
                <ProgressBar now={100} style={{ height: "7px" }} />
              </div>
            </Col>
            <Col>
              <div
                style={{
                  padding: "5px",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
                className={`tab_box tab_box_critical ${
                  selectTab === "criticals" ? "tab_box_critical_active" : ""
                }`}
                onClick={() => {
                  setSelectTab("criticals");
                  setActiveSide(null);
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "0.5rem",
                  }}
                >
                  <div>
                    <BiSolidUpArrow
                      style={{ marginRight: "5px", color: "#dc3545" }}
                    />
                    {criticalCollected.reduce(
                      (sum, issue) => sum + issue.value,
                      0
                    )}{" "}
                    Criticals
                  </div>
                  <div style={{ marginLeft: "auto" }}>
                    {criticalPercentage}%
                  </div>
                </div>
                <ProgressBar
                  now={Number(criticalPercentage) || 0}
                  style={{ height: "7px" }}
                  variant="danger"
                />
              </div>
            </Col>
            <Col>
              <div
                style={{
                  padding: "5px",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
                className={`tab_box tab_box_warning ${
                  selectTab === "warnings" ? "tab_box_warning_active" : ""
                }`}
                onClick={() => {
                  setSelectTab("warnings");
                  setActiveSide(null);
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "0.5rem",
                  }}
                >
                  <div>
                    <FaSquare
                      style={{ marginRight: "5px", color: "#ffc107" }}
                    />
                    {warningCollected.reduce(
                      (sum, issue) => sum + issue.value,
                      0
                    )}{" "}
                    Warnings
                  </div>
                  <div style={{ marginLeft: "auto" }}>{warningPercentage}%</div>
                </div>
                <ProgressBar
                  now={Number(warningPercentage) || 0}
                  style={{ height: "7px" }}
                  variant="warning"
                />
              </div>
            </Col>
            <Col>
              <div
                style={{
                  padding: "5px",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
                className={`tab_box tab_box_notices ${
                  selectTab === "notices" ? "tab_box_notices_active" : ""
                }`}
                onClick={() => {
                  setSelectTab("notices");
                  setActiveSide(null);
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "0.5rem",
                  }}
                >
                  <div>
                    <FaCircle
                      style={{ marginRight: "5px", color: "#6c757d" }}
                    />
                    {noticesCollected.reduce(
                      (sum, issue) => sum + issue.value,
                      0
                    )}{" "}
                    Notices
                  </div>
                  <div style={{ marginLeft: "auto" }}>{noticesPercentage}%</div>
                </div>
                <ProgressBar
                  now={Number(noticesPercentage) || 0}
                  style={{ height: "7px" }}
                  variant="secondary"
                />
              </div>
            </Col>
            <Col>
              <div
                style={{
                  padding: "5px",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
                className={`tab_box tab_box_passed ${
                  selectTab === "passed" ? "tab_box_passed_active" : ""
                }`}
                onClick={() => {
                  setSelectTab("passed");
                  setActiveSide(null);
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "0.5rem",
                  }}
                >
                  <div>
                    {passedCollected.reduce(
                      (sum, issue) => sum + issue.value,
                      0
                    )}{" "}
                    Passed
                  </div>
                  <div style={{ marginLeft: "auto" }}>{passedPercentage}%</div>
                </div>
                <ProgressBar
                  now={Number(passedPercentage) || 0}
                  style={{ height: "7px" }}
                  variant="success"
                />
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
    </Card>
  );
};
